import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserGroup, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useOrganizationImpersonationContext } from "../../../../contexts/OrganizationImpersonationContext";
import { enviroment } from "../../../../enviroment";
import { CLARITY_MASK } from "../../../../utils/analytics/clarityAttrs";

// The banner strip has a fixed height so the layout can reserve exactly this
// much extra top padding for it (see IMPERSONATION_BANNER_HEIGHT_REM /
// App.tsx). Content is ~33px; h-9 = 36px gives it deterministic room.
export const IMPERSONATION_BANNER_HEIGHT_REM = 2.25;

// Single source of truth for "does the banner render?" — App.tsx uses this to
// reserve layout space, so banner and padding can never diverge. Must stay in
// exact sync with the render gate inside ImpersonationBanner below.
export function useImpersonationBannerVisible(): boolean {
  const { organizationId } = useOrganizationImpersonationContext();
  return enviroment.actAsVendorEnabled && organizationId != null;
}

// Full-width strip under the nav bar while an admin is acting as a vendor user.
// Always visible so it is impossible to forget which identity is active.
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { vendor, user, setImpersonation } =
    useOrganizationImpersonationContext();
  const visible = useImpersonationBannerVisible();

  // Deliberately NOT gated on isImpersonatingAdmin: on a hard reload while
  // impersonating, the first profile fetched is the VENDOR's (role=Vendor), so
  // the sticky admin flag never turns true — the exit path must still render.
  // Active impersonation state can only be created by an admin (the API rejects
  // everyone else), so state-present is the honest gate.
  if (!visible) {
    return null;
  }

  const userName = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
    : "";

  function handleExit() {
    // No cart clearing here: CartProvider's scope-switch effect parks the
    // vendor cart and restores the admin's own — clearing would delete it.
    setImpersonation(undefined, undefined);
  }

  return (
    <div
      className={
        // h-9 (= IMPERSONATION_BANNER_HEIGHT_REM) instead of py: the strip's
        // height must be deterministic because App.tsx reserves exactly this
        // much layout space under the fixed header.
        "w-full h-9 flex items-center bg-amber-100 border-b border-amber-300 text-amber-900 px-8"
      }
      data-testid="impersonation-banner"
    >
      <div className="max-w-[90rem] w-full mx-auto flex items-center gap-x-3 text-sm">
        <FontAwesomeIcon icon={faUserGroup} />
        <span className="font-semibold whitespace-nowrap">
          {t("headerFooter.actingAs")}
        </span>
        <span className="truncate" {...CLARITY_MASK}>
          {userName}
          {userName && vendor?.name ? " — " : ""}
          {vendor?.name ?? ""}
        </span>
        <button
          type="button"
          onClick={handleExit}
          className={
            "ml-auto flex items-center gap-1.5 rounded-full border border-amber-400 " +
            "px-3 py-0.5 text-xs font-semibold uppercase hover:bg-amber-200 whitespace-nowrap"
          }
        >
          <FontAwesomeIcon icon={faXmark} />
          {t("headerFooter.resetToAdmin")}
        </button>
      </div>
    </div>
  );
}
